import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';

interface PageTitleValue {
  title: string | null;
  setTitle: (title: string | null) => void;
}

const PageTitleContext = createContext<PageTitleValue | null>(null);

/**
 * Holds the heading the active page wants shown in the mobile top bar. Mounted in the app shell so
 * the bar (which lives above the routed outlet) can read what the page below it registered.
 */
export function PageTitleProvider({ children }: { children: ReactNode }) {
  const [title, setTitle] = useState<string | null>(null);
  return (
    <PageTitleContext.Provider value={{ title, setTitle }}>{children}</PageTitleContext.Provider>
  );
}

/** The active page's registered heading, or null before any page sets one. */
export function usePageTitleValue(): string | null {
  return useContext(PageTitleContext)?.title ?? null;
}

/** Register the active page's heading so the mobile top bar can show it in place of the brand. */
export function usePageTitle(title: string): void {
  const setTitle = useContext(PageTitleContext)?.setTitle;
  useEffect(() => {
    setTitle?.(title);
    return () => setTitle?.(null);
  }, [setTitle, title]);
}
